import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, dateShort, timeAgo, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Thumb, ClientStatusBadge, SelectionBadge, Pill, Field } from "@/components/Bits";
import ApprovalActions from "@/components/ApprovalActions";
import PresentationGallery from "@/components/PresentationGallery";
import ProjectNav from "@/components/ProjectNav";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";

export default function PresentationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const [pres, setPres] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/presentations/${id}`);
      setPres(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!pres) return <div className="py-24 text-center text-sm text-fog">Loading presentation…</div>;

  const awaiting = (pres.selections || []).filter((s) => s.selection_status === "Awaiting Approval");
  const canApproveAll = ["whole", "both"].includes(pres.approval_mode) && awaiting.length > 0;
  const allVisuals = pres.visuals || [];
  const visuals = isStaff && !preview ? allVisuals : allVisuals.filter((v) => v.visible_to_client);
  const blocks = (pres.blocks || []).filter((b) => b.type !== "selection_group");

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/presentations/${id}/publish`);
      toast.success("Published to client");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approveAll = async () => {
    if (!window.confirm(`Approve all ${awaiting.length} selection(s) in this design?`)) return;
    setBusy(true);
    try {
      await api.post(`/presentations/${id}/approve`);
      toast.success("Design approved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendFeedback = async () => {
    if (!feedback.trim()) return;
    try {
      await api.post(`/presentations/${id}/feedback`, { body: feedback });
      setFeedback("");
      toast.success("Feedback sent to your designer");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-12 pb-10" data-testid="presentation-detail-page">
      <ProjectNav
        projectId={pres.project_id}
        projectName={pres.project_name || "Project"}
        activeSection="Presentations"
        trail={[
          { label: "Presentations", to: `/projects/${pres.project_id}?tab=Presentations` },
          { label: pres.name || "Presentation" },
        ]}
      />
      <Link
        to={`/projects/${pres.project_id}?tab=Presentations`}
        data-testid="back-to-presentations"
        className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone transition-colors hover:border-lime hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back to Presentation Overview
      </Link>

      {isStaff && (
        <div className="card-surface flex flex-wrap items-center justify-between gap-4 p-5" data-testid="presentation-admin-bar">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Pill tone={pres.status === "Published" ? "bg-lime text-ink" : "bg-line text-stone"}>{pres.status}</Pill>
            {preview && <Pill tone="bg-[#EDEFC4] text-ink">Client preview</Pill>}
            {pres.published_at && <span className="text-xs text-fog">Published {dateShort(pres.published_at)}</span>}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link data-testid="presentation-preview-btn" to={`/presentations/${id}?preview=1`} className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-sm text-ink hover:bg-line/50">
              <Eye className="h-4 w-4" strokeWidth={1.5} /> Preview
            </Link>
            <Link data-testid="presentation-edit-btn" to={`/presentations/${id}/edit`} className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-sm text-ink hover:bg-line/50">
              <Pencil className="h-4 w-4" strokeWidth={1.5} /> Edit
            </Link>
            <button data-testid="presentation-publish-btn" onClick={publish} disabled={busy} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60">
              <Send className="h-4 w-4" strokeWidth={1.5} /> {pres.status === "Published" ? "Re-publish" : "Publish to Client"}
            </button>
          </div>
        </div>
      )}

      <div className="card-surface overflow-hidden">
        {pres.cover_image || pres.hero_image ? (
          <img src={pres.cover_image || pres.hero_image} alt={pres.title} className="aspect-[21/9] w-full object-cover" />
        ) : (
          <Thumb className="aspect-[21/9] w-full" rounded="rounded-none" />
        )}
        <div className="p-8 sm:p-12">
          <div className="overline mb-4">{pres.project_name}{pres.space_name ? ` · ${pres.space_name}` : ""}</div>
          <h1 data-testid="presentation-title" className="font-serif text-4xl tracking-tight text-ink sm:text-5xl">
            {pres.title || pres.name}
          </h1>
          {pres.narrative && (
            <p data-testid="presentation-narrative" className="mt-7 max-w-3xl text-base leading-relaxed text-stone">
              {pres.narrative}
            </p>
          )}
        </div>
      </div>

      {blocks.length > 0 ? (        <div className="space-y-14" data-testid="presentation-blocks">
          {blocks.map((b) => {
            if (b.type === "text") {
              return (
                <section key={b.id} className="max-w-3xl" data-testid={`block-text-${b.id}`}>
                  {b.heading && <h2 className="font-serif text-2xl text-ink sm:text-3xl">{b.heading}</h2>}
                  {b.body && <p className="mt-5 whitespace-pre-wrap text-base leading-relaxed text-stone">{b.body}</p>}
                </section>
              );
            }
            if (b.type === "visual") {
              const v = visuals.find((x) => x.id === b.visual_id);
              return v ? (
                <div key={b.id} data-testid={`block-visual-${b.id}`}>
                  <PresentationGallery visuals={[v]} heading={null} />
                </div>
              ) : null;
            }
            if (b.type === "product_board") {
              return (
                <section key={b.id} data-testid={`block-product-board-${b.id}`}>
                  <SectionTitle>Product Board</SectionTitle>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {(pres.selections || []).map((p) => (
                      <figure key={p.id} className="card-surface overflow-hidden">
                        <Thumb src={productImage(p)} alt={p.name} className="aspect-square w-full" rounded="rounded-none" />
                        <figcaption className="p-4">
                          <div className="overline">{p.category}</div>
                          <div className="mt-1 truncate text-sm text-ink">{p.name}</div>
                          {p.client_total != null && <div className="num mt-1 text-xs text-stone">{money(p.client_total)}</div>}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </section>
              );
            }
            return null;
          })}
        </div>
      ) : (
        <PresentationGallery visuals={visuals} />
      )}

      {pres.pinterest_board && (isStaff || pres.pinterest_board.visible_to_client) && (
        <section className="card-surface flex flex-wrap items-center justify-between gap-5 p-8" data-testid="pinterest-board-block">
          <div>
            <div className="overline">More Inspiration</div>
            <h3 className="mt-3 font-serif text-2xl text-ink">{pres.pinterest_board.title}</h3>
            <p className="mt-2 text-sm text-stone">Explore the inspiration board behind this scheme.</p>
            {isStaff && !pres.pinterest_board.visible_to_client && (
              <div className="mt-3">
                <Pill tone="bg-line text-stone">Internal only — the client cannot see this</Pill>
              </div>
            )}
          </div>
          <a
            href={pres.pinterest_board.url}
            target="_blank"
            rel="noreferrer"
            data-testid="view-pinterest-board"
            className="inline-flex items-center gap-2 rounded-sm bg-ink px-5 py-3 text-sm text-white transition-colors hover:bg-stone"
          >
            View Pinterest Board →
          </a>
        </section>
      )}

      <section>
        <SectionTitle
          action={
            canApproveAll && (
              <button data-testid="approve-design-btn" onClick={approveAll} disabled={busy} className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60">
                Approve Design
              </button>
            )
          }
        >
          Your Selections
        </SectionTitle>
        <div className="grid gap-8 lg:grid-cols-2" data-testid="presentation-selections">
          {(pres.selections || []).map((p) => (
            <article key={p.id} className="card-surface overflow-hidden" data-testid={`presentation-selection-${p.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-line">
                {productImage(p) ? (
                  <img src={productImage(p)} alt={p.name} className="h-full w-full object-cover" />
                ) : (
                  <Thumb className="h-full w-full" rounded="rounded-none" />
                )}
              </div>
              <div className="space-y-4 p-6 sm:p-7">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="overline">{p.category}</span>
                  {isStaff ? <SelectionBadge status={p.selection_status} /> : <ClientStatusBadge status={p.client_status} />}
                </div>
                <h3 className="font-serif text-2xl leading-snug text-ink">{p.name}</h3>
                {p.description && <p className="text-sm leading-relaxed text-stone">{p.description}</p>}
                <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  {p.client_total != null && (
                    <div>
                      <div className="overline">Investment</div>
                      <div className="num mt-1 text-ink">{money(p.client_total)}</div>
                    </div>
                  )}
                  {p.dimensions && (
                    <div>
                      <div className="overline">Dimensions</div>
                      <div className="mt-1 text-ink">{p.dimensions}</div>
                    </div>
                  )}
                  {p.lead_time && (
                    <div>
                      <div className="overline">Lead Time</div>
                      <div className="mt-1 text-ink">{p.lead_time}</div>
                    </div>
                  )}
                </div>
                <ApprovalActions product={p} onChanged={load} />
              </div>
            </article>
          ))}
          {!pres.selections?.length && <p className="text-sm text-fog">No selections in this presentation yet.</p>}
        </div>
      </section>

      <section className="max-w-3xl">
        <SectionTitle>Overall Feedback</SectionTitle>
        <div className="space-y-4" data-testid="presentation-feedback">
          {(pres.feedback || []).map((f) => (
            <div key={f.id} className="card-surface p-5">
              <p className="text-sm leading-relaxed text-ink">“{f.body}”</p>
              <div className="mt-2 text-xs text-fog">{f.author_name} · {timeAgo(f.created_at)}</div>
            </div>
          ))}
        </div>
        <Field label="Share your thoughts on this design" className="mt-6">
          <textarea data-testid="presentation-feedback-input" rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} className={input} />
        </Field>
        <button data-testid="presentation-feedback-btn" onClick={sendFeedback} className="mt-4 rounded-sm bg-ink px-5 py-2.5 text-sm text-white hover:bg-stone">
          Send Feedback
        </button>
      </section>
    </div>
  );
}
